import React from "react";
import { useTranslations } from "next-intl";
import Head from "next/head";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Page from "@components/Layout/page";
import { Leaf } from "@components/Leaf/Leaf";
import { useInView } from "@hooks/useInView";
import styles from "./MatarsounPage.module.scss";

export default function MatarsounPage() {
    const t = useTranslations("MatarsounPage");

    const [heroRef, heroInView] = useInView();
    const [measureRef, measureInView] = useInView();
    const [resultsRef, resultsInView] = useInView();
    const [ctaRef, ctaInView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">{"Matarsóun - " + WEBPAGE_TITLE}</title>
                <meta
                    name="keywords"
                    content="Matarsóun, mötuneyti, sóun, matur, hagræðing"
                />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Matarsóun`}
                    key="og:title"
                />
                <meta
                    property="og:description"
                    content={t("hero_desc")}
                    key="og:description"
                />
                <meta name="description" content={t("hero_desc")} />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />
                <Leaf className={styles.leafBg3} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>{t("badge")}</span>
                        <h1 className={styles.heroTitle}>{t("hero_title")}</h1>
                        <p className={styles.heroSubtitle}>
                            {t("hero_subtitle")}
                        </p>
                        <p className={styles.heroDesc}>{t("hero_desc")}</p>
                        <a className={styles.ctaBtn} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>

                <div className={styles.showcase}>
                    <section
                        ref={measureRef}
                        className={`${styles.showcaseRow} ${styles.revealTarget} ${measureInView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_measure_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_measure_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_measure_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.statGrid}>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="12" cy="12" r="9" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_plate")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_plate_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M3 11h18a9 9 0 0 1-18 0z" />
                                            <line
                                                x1="12"
                                                y1="3"
                                                x2="12"
                                                y2="7"
                                            />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_serving")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_serving_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <rect
                                                x="6"
                                                y="2"
                                                width="12"
                                                height="20"
                                                rx="2"
                                            />
                                            <line
                                                x1="6"
                                                y1="10"
                                                x2="18"
                                                y2="10"
                                            />
                                            <line x1="9" y1="6" x2="9" y2="7" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_kitchen")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_kitchen_desc")}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.statCard} ${styles.statCardAccent}`}
                                >
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <line
                                                x1="18"
                                                y1="20"
                                                x2="18"
                                                y2="10"
                                            />
                                            <line
                                                x1="12"
                                                y1="20"
                                                x2="12"
                                                y2="4"
                                            />
                                            <line
                                                x1="6"
                                                y1="20"
                                                x2="6"
                                                y2="14"
                                            />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_baseline")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_baseline_desc")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section
                        ref={resultsRef}
                        className={`${styles.showcaseRow} ${styles.reversed} ${styles.revealTarget} ${resultsInView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_results_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_results_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_results_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.resultStack}>
                                <div className={styles.resultCard}>
                                    <div className={styles.resultIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <line
                                                x1="18"
                                                y1="20"
                                                x2="18"
                                                y2="10"
                                            />
                                            <line
                                                x1="12"
                                                y1="20"
                                                x2="12"
                                                y2="4"
                                            />
                                            <line
                                                x1="6"
                                                y1="20"
                                                x2="6"
                                                y2="14"
                                            />
                                        </svg>
                                    </div>
                                    <div className={styles.resultContent}>
                                        <span className={styles.resultLabel}>
                                            {t("result_compare")}
                                        </span>
                                        <span className={styles.resultMeta}>
                                            {t("result_compare_desc")}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.resultCard}>
                                    <div className={styles.resultIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                        </svg>
                                    </div>
                                    <div className={styles.resultContent}>
                                        <span className={styles.resultLabel}>
                                            {t("result_tracking")}
                                        </span>
                                        <span className={styles.resultMeta}>
                                            {t("result_tracking_desc")}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.resultCard}>
                                    <div className={styles.resultIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <line
                                                x1="12"
                                                y1="1"
                                                x2="12"
                                                y2="23"
                                            />
                                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                                        </svg>
                                    </div>
                                    <div className={styles.resultContent}>
                                        <span className={styles.resultLabel}>
                                            {t("result_savings")}
                                        </span>
                                        <span className={styles.resultMeta}>
                                            {t("result_savings_desc")}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <section
                    ref={ctaRef}
                    className={`${styles.bottomCta} ${styles.revealTarget} ${ctaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.bottomCtaContent}>
                        <h2 className={styles.bottomCtaTitle}>
                            {t("bottom_title")}
                        </h2>
                        <p className={styles.bottomCtaDesc}>
                            {t("bottom_desc")}
                        </p>
                        <a className={styles.ctaBtnLight} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>
            </div>
        </Page>
    );
}
